import { useIntParam } from 'hooks';
import { useMemo } from 'react';
import { useLocation } from 'react-router';
import { speakingSessionRoomStructureStore } from 'store';
import { useSpeakingSessionDetails } from '../useSpeakingSessionDetails';
import { calculateExaminersAvailability } from './utils/examinersAvailabilityCalculator';
import { getStructureDateTimeRange } from './utils/getStructureDateTimeRange';
import { useExaminerAvailabilities } from '../contexts/ExaminerAvailabilitiesContext';

export type ManageExaminerToRoomStructureRouteState = {
  structureStartTime?: string;
  structureEndTime?: string;
  sessionRoomId?: number;
  sessionRoomStructureId?: number;
  assignedExaminerId?: number;
};

export const useManageExaminersToRoomStructure = () => {
  const speakingSessionId = useIntParam('sessionId');

  const { data: examiners, isFetching: isFetchingExaminers } =
    speakingSessionRoomStructureStore.service.speakingSessionRoomStructureApi.useGetExaminersQuery({
      speakingSessionId,
    });

  const { data: examinersAllocations, isFetching: isFetchingAllocations } =
    speakingSessionRoomStructureStore.service.speakingSessionRoomStructureApi.useGetExaminersAllocationsQuery(
      {
        speakingSessionId,
      }
    );

  const { examinersAvailabilities, isFetchingAvailabilities } = useExaminerAvailabilities();

  const location = useLocation();
  const routeState = (location.state ?? {}) as ManageExaminerToRoomStructureRouteState;

  const isFetching = isFetchingExaminers || isFetchingAllocations || isFetchingAvailabilities;
  const speakingSessionDate = useSpeakingSessionDetails().speakingSession?.date;

  const availableExaminers = useMemo(() => {
    if (
      isFetching ||
      !speakingSessionDate ||
      !routeState.structureStartTime ||
      !routeState.structureEndTime
    )
      return [];

    const { start: structureStartDateTime, end: structureEndDateTime } = getStructureDateTimeRange(
      speakingSessionDate,
      routeState.structureStartTime,
      routeState.structureEndTime
    );

    return calculateExaminersAvailability(
      examiners || [],
      examinersAllocations || [],
      examinersAvailabilities || [],
      structureStartDateTime,
      structureEndDateTime,
      routeState.assignedExaminerId
    );
  }, [
    examiners,
    examinersAllocations,
    examinersAvailabilities,
    isFetching,
    routeState.assignedExaminerId,
    routeState.structureEndTime,
    routeState.structureStartTime,
    speakingSessionDate,
  ]);

  return {
    availableExaminers,
    isFetching,
    routeState,
  };
};
